import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MarketplaceShell from './MarketplaceShell';
import MarketplaceErrorScreen from './MarketplaceErrorScreen';
import StepIndicator, { WIZARD_STEP_LABELS } from './StepIndicator';
import { UseCaseCard, UseCaseCardSkeleton, USE_CASE_META } from './useCaseMeta';
import { MktCard, Pill, Tag, Checkbox, PriceTag, WizardNav, PillSkeletons } from './marketplaceUi';
import { saveDraft } from './wizardDraft';
import { MARKETPLACE_FILL_PATH, MARKETPLACE_HOME_PATH } from './marketplaceLinks';
import {
  getUseCases, getSubCategories, getArchetypes, getMarketplaceState, getSelectionTwinCount,
  dollars, type UseCase, type SubCategory, type Archetype, type StudySelection,
  type TwinCountResult,
} from './marketplaceApi';

// Debounce window before asking the server to re-count the current selection.
// Matches the 300ms filter-debounce used in UnifiedSegmentsSelector.
const COUNT_DEBOUNCE_MS = 300;

// Soft guidance only (never blocks): below this many Twins, results carry a
// low-sample reliability caveat.
const MIN_RECOMMENDED_TWINS = 30;

/**
 * Paid study — ported from pay_per_study.html. Two internal screens:
 * (1) choose a use case, (2) build an audience by selecting sub-categories and
 * archetypes (checkbox model — each selected archetype contributes all its
 * Twins).
 *
 * Cost is billed per DISTINCT profile. Because a persona can belong to several
 * breakfast categories at once, the grand-total Twin count and price come from
 * the server (which de-dupes across the selection) — NEVER from summing
 * per-archetype bucket sizes, which would over-count and over-charge.
 */
const BuildAudienceScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = (location.state || {}) as {
    useCase?: string;
    // Carried back from Set-up/Review (see FillStudyScreen.backToAudienceStep) so a
    // paid user who presses Back does NOT lose their built audience or spawn a
    // duplicate study. The free flow restores the same way in UseCasePicker.
    selections?: StudySelection[];
    twinCount?: number;
    studyId?: number;
  };
  // Rebuild the builder's slug -> archetype-id Set model from the carried
  // selections, computed once at mount (empty for a fresh build → identical to the
  // prior behaviour).
  const restoredRef = useRef<{ sel: Record<string, Set<string>>; cats: string[] } | null>(null);
  if (restoredRef.current === null) {
    const sel: Record<string, Set<string>> = {};
    const cats: string[] = [];
    for (const s of preset.selections ?? []) {
      if (!s.archetype_id) continue;
      (sel[s.sub_category] ??= new Set()).add(s.archetype_id);
      if (!cats.includes(s.sub_category)) cats.push(s.sub_category);
    }
    restoredRef.current = { sel, cats };
  }
  const restored = restoredRef.current;
  // Deep-linked with a use case (e.g. a returning user from a dashboard card) →
  // skip straight to the audience builder.
  // The internal step lives in the URL (?step=audience) — the same pattern as
  // UseCasePicker — so setSearchParams PUSHES a history entry and the browser
  // Back button moves between the two screens instead of leaving the wizard
  // entirely and silently resetting it (#5/#73). Component state here meant Back
  // popped the single /build entry, remounting the screen at step 1.
  const [searchParams, setSearchParams] = useSearchParams();
  const screen: 1 | 2 = searchParams.get('step') === 'audience' ? 2 : 1;
  const setScreen = (n: 1 | 2) => setSearchParams(n === 2 ? { step: 'audience' } : {});

  // Deep-linked with a use case → jump straight to the audience step.
  useEffect(() => {
    if (preset.useCase && searchParams.get('step') !== 'audience') {
      setSearchParams({ step: 'audience' }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [unitPrice, setUnitPrice] = useState(500);
  const [useCase, setUseCase] = useState<string | null>(preset.useCase || null);
  const [activeCats, setActiveCats] = useState<string[]>(restored.cats);
  const [cache, setCache] = useState<Record<string, Archetype[]>>({});
  // sub-category slug -> set of selected archetype_ids
  const [sel, setSel] = useState<Record<string, Set<string>>>(restored.sel);
  // Reuse the study created on a prior pass (Back from Review) instead of creating
  // a second one on re-launch, which would orphan the first as an undeletable draft.
  const [studyId] = useState<number | undefined>(preset.studyId);
  const [error, setError] = useState<string | null>(null);
  // Server-resolved DISTINCT count (grand total + per-sub-category) tagged with
  // the selection it was computed for, so a still-in-flight recount is never
  // mistaken for the current selection's authoritative count.
  const [counted, setCounted] = useState<{ key: string; result: TwinCountResult } | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  // /sub-categories is slow (measured 3.6s, once 15.6s) — without this the pill
  // row renders as an empty void.
  const [subsLoading, setSubsLoading] = useState(true);
  const countTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCountKey = useRef<string>('');

  // Independent fetches (see UseCasePicker): the use-case cards must not sit in
  // skeleton state behind the slow /sub-categories call, which screen 2 is the
  // first to read.
  useEffect(() => {
    Promise.all([getUseCases(), getMarketplaceState()])
      .then(([u, st]) => {
        setUseCases(u);
        setUnitPrice(st.twin_unit_price_cents);
        // Preselect the first card (see UseCasePicker) — a deep-linked preset wins.
        setUseCase((current) => current || u[0]?.key || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMeta(false));

    getSubCategories()
      .then((s) => {
        setSubs(s);
        // Restoring a carried audience: activate + fetch every restored sub-category
        // so `totals` can rebuild the selection. Fresh build: pre-activate just the
        // first sub-category (unchanged behaviour).
        if (restored.cats.length > 0) restored.cats.forEach((slug) => loadCat(slug, true));
        else if (s[0]) loadCat(s[0].slug, true);
      })
      .catch(() => setSubs([]))
      .finally(() => setSubsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCat = (slug: string, activate: boolean) => {
    if (activate) setActiveCats((c) => (c.includes(slug) ? c : [...c, slug]));
    if (!cache[slug]) {
      getArchetypes(slug)
        .then((a) => setCache((c) => ({ ...c, [slug]: a })))
        // Non-fatal: a failed category load leaves that category empty rather
        // than replacing the whole builder (and losing the paid selection).
        .catch(() => setCache((c) => ({ ...c, [slug]: [] })));
    }
  };

  const toggleCat = (slug: string) => {
    if (activeCats.includes(slug)) {
      setActiveCats((c) => c.filter((s) => s !== slug));
      setSel((s) => { const n = { ...s }; delete n[slug]; return n; });
    } else {
      loadCat(slug, true);
      setSel((s) => ({ ...s, [slug]: s[slug] || new Set() }));
    }
  };

  // Start with a non-zero selection (mockup never shows "0 selected"): once the
  // pre-activated sub-category's archetypes load, select its first archetype.
  // Skip the "auto-select the first archetype" nicety when we've restored a real
  // selection — otherwise it would tack an extra archetype onto the restored one.
  const preselected = useRef(restored.cats.length > 0);
  useEffect(() => {
    if (preselected.current) return;
    const first = activeCats[0];
    const archs = first ? cache[first] : undefined;
    if (!first || !archs || archs.length === 0) return;
    preselected.current = true;
    setSel((s) => (s[first] && s[first].size > 0 ? s : { ...s, [first]: new Set([archs[0].archetype_id]) }));
  }, [cache, activeCats]);

  const archsOf = (slug: string) => cache[slug] || [];
  const isAll = (slug: string) => {
    const a = archsOf(slug);
    return a.length > 0 && a.every((x) => sel[slug]?.has(x.archetype_id));
  };
  const toggleAll = (slug: string) => {
    setSel((s) => {
      const next = { ...s };
      next[slug] = isAll(slug) ? new Set() : new Set(archsOf(slug).map((a) => a.archetype_id));
      return next;
    });
  };
  const toggleArch = (slug: string, id: string) => {
    setSel((s) => {
      const set = new Set(s[slug] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...s, [slug]: set };
    });
  };

  const totals = useMemo(() => {
    // `provisionalTwins` is a naive per-archetype sum used ONLY as a placeholder
    // until the server returns the authoritative distinct count (correct while a
    // single archetype is selected — no overlap yet). The displayed grand total
    // and price below come from the server's distinct `counted` result, never from
    // this sum: personas can belong to multiple categories, so summing would
    // double-count and overcharge.
    let provisionalTwins = 0;
    const tags: { label: string; n: number }[] = [];
    const selections: StudySelection[] = [];
    activeCats.forEach((slug) => {
      const archs = archsOf(slug);
      const set = sel[slug];
      if (!set || set.size === 0) return;
      const label = subs.find((s) => s.slug === slug)?.name || slug;
      if (isAll(slug)) {
        // Within one category the archetype buckets are disjoint (a persona has a
        // single cohort per category), so this per-category sum is exact.
        const n = archs.reduce((acc, a) => acc + a.twin_count, 0);
        tags.push({ label: `${label} · All`, n });
      }
      archs.forEach((a) => {
        if (!set.has(a.archetype_id)) return;
        provisionalTwins += a.twin_count;
        selections.push({ sub_category: slug, archetype_id: a.archetype_id, twin_count: a.twin_count, name: a.name });
        if (!isAll(slug)) tags.push({ label: a.name, n: a.twin_count });
      });
    });
    return { provisionalTwins, tags, selections };
  }, [activeCats, sel, cache, subs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced distinct-count fetch. A stable primitive key (sorted archetype ids)
  // means we only re-fetch when the selection actually changes, and the cleanup
  // cancels any in-flight timer so rapid clicks collapse into one call.
  const selectionKey = totals.selections.map((s) => s.archetype_id).sort().join('|');
  useEffect(() => {
    if (countTimeout.current) clearTimeout(countTimeout.current);
    const selections = totals.selections;
    if (selections.length === 0) {
      setCounted({ key: '', result: { twin_count: 0, by_sub_category: {} } });
      setCountLoading(false);
      return;
    }
    setCountLoading(true);
    latestCountKey.current = selectionKey;
    countTimeout.current = setTimeout(() => {
      getSelectionTwinCount(selections.map((s) => ({ sub_category: s.sub_category, archetype_id: s.archetype_id })))
        .then((r) => setCounted({ key: selectionKey, result: r }))
        // A failed recount is non-fatal: keep the provisional count (never 0
        // while something is selected) rather than wiping the whole builder.
        .catch(() => {})
        // Only clear the spinner if this is still the newest selection — a stale
        // in-flight recount must not un-dim while a newer one is pending.
        .finally(() => { if (latestCountKey.current === selectionKey) setCountLoading(false); });
    }, COUNT_DEBOUNCE_MS);
    return () => { if (countTimeout.current) clearTimeout(countTimeout.current); };
  }, [selectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Authoritative distinct figures once the server count for THIS selection is in.
  // Until then, fall back to the provisional sum — which is exact for a single
  // archetype/category (no overlap) and, crucially, never 0 while something is
  // selected, so the total never flashes to 0 during a recount (we only dim it).
  const freshCount = counted?.key === selectionKey ? counted.result : null;
  const twins = freshCount ? freshCount.twin_count : totals.provisionalTwins;
  const cost = twins * unitPrice;
  // Don't allow starting while a distinct recount is still in flight — the
  // provisional (pre-dedup) count would over-bill; wait for the authoritative one.
  const canStart = !!useCase && twins > 0 && !countLoading;
  const meta = useCase ? USE_CASE_META[useCase] : null;

  const onStart = () => {
    if (!canStart) return;
    // Persist so a reload on the fill/review step rebuilds the wizard (#73).
    saveDraft({ useCase: useCase!, selections: totals.selections, twinCount: twins, paid: true, studyId, savedFormData: undefined });
    navigate(MARKETPLACE_FILL_PATH, { state: { useCase, selections: totals.selections, twinCount: twins, paid: true, studyId } });
  };

  if (error) return (
    <MarketplaceErrorScreen
      title="We couldn’t load this page"
      message="Something went wrong setting up your study. Please try again."
      onRetry={() => window.location.reload()}
    />
  );

  return (
    <MarketplaceShell>
      <StepIndicator step={screen} labels={WIZARD_STEP_LABELS} />

      {/* ── Screen 1: use case (flow entry — no back affordance) ────── */}
      {screen === 1 && (
        <>
          <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-[22px] font-medium text-[#111]">What kind of study do you want to run?</h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">Pick one use case. You can run a different one next time.</p>
            </div>
            <PriceTag cents={unitPrice} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {loadingMeta
              ? Array.from({ length: 4 }).map((_, i) => <UseCaseCardSkeleton key={i} />)
              : useCases.map((uc) => (
                  <UseCaseCard key={uc.key} useCaseKey={uc.key} selected={useCase === uc.key} onClick={() => setUseCase(uc.key)} freeAvailable={false} />
                ))}
          </div>

          <WizardNav
            onBack={() => navigate(MARKETPLACE_HOME_PATH)}
            onContinue={() => setScreen(2)}
            continueDisabled={!useCase}
            hint={!useCase ? 'Choose a use case to continue' : undefined}
          />
        </>
      )}

      {/* ── Screen 2: audience builder ─────────────────────────────── */}
      {screen === 2 && meta && (
        <>
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-[22px] font-medium text-[#111]">Build your audience</h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">Select Breakfast Categories and Segments. Cost updates as you build.</p>
            </div>
            <PriceTag cents={unitPrice} />
          </div>

          <MktCard className="p-5">
            <div className="mb-2.5 flex items-baseline justify-between">
              <div className="text-[12px] text-[#6b6b6b]">Category</div>
              <div className="text-[12px] text-[#6b6b6b]">Select one or more</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {subsLoading && subs.length === 0 && <PillSkeletons count={6} />}
              {subs.map((sc) => (
                <Pill key={sc.slug} active={activeCats.includes(sc.slug)} showCheck onClick={() => toggleCat(sc.slug)}>
                  {sc.name}
                </Pill>
              ))}
            </div>

            <div className="my-5 h-px bg-black/10" />

            {/* Per-category archetype blocks */}
            <div className="mb-6 flex flex-col gap-2.5">
              {activeCats.map((slug) => {
                const archs = archsOf(slug);
                const set = sel[slug] || new Set();
                const label = subs.find((s) => s.slug === slug)?.name || slug;
                // The whole-category total is the sub-category's own distinct Twin
                // count from the catalog (authoritative), not a bucket sum.
                const total = subs.find((s) => s.slug === slug)?.twin_count
                  ?? archs.reduce((n, a) => n + a.twin_count, 0);
                // Selected-in-category: prefer the server's per-category distinct
                // count; fall back to the bucket sum (exact within a category, whose
                // cohorts are disjoint) until the count resolves.
                const selTwins = freshCount?.by_sub_category?.[slug]
                  ?? archs.reduce((n, a) => n + (set.has(a.archetype_id) ? a.twin_count : 0), 0);
                const allSel = isAll(slug);
                // `cache[slug]` is undefined until THAT category's archetypes land
                // (archsOf() silently returns [] meanwhile), so an activated category
                // rendered an empty body with no sign it was still loading.
                const archsLoading = cache[slug] === undefined;
                return (
                  <div key={slug} className="overflow-hidden rounded-xl border border-black/10">
                    <div className="flex items-center justify-between gap-3 border-b border-black/10 bg-[#f5f5f3] px-4 py-3">
                      <div className="min-w-0 truncate text-[13px] font-medium text-[#111]">{label}</div>
                      <div className="flex-shrink-0 whitespace-nowrap text-[12px] text-[#6b6b6b]">
                        {archsLoading
                          ? 'Loading Segments…'
                          : <>{set.size} of {archs.length} selected · <span className="font-medium text-[#111]">{selTwins} Twins</span></>}
                      </div>
                    </div>
                    {archsLoading && (
                      <div className="flex flex-wrap gap-2 p-4"><PillSkeletons count={4} /></div>
                    )}
                    {/* All row */}
                    <div
                      onClick={() => toggleAll(slug)}
                      className={`flex cursor-pointer items-center justify-between border-b border-black/10 px-4 py-2.5 ${allSel ? 'bg-[#E8FCFA]' : 'bg-[#f5f5f3] hover:bg-[#ececea]'}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Checkbox checked={allSel} />
                        <span className="text-[13px] font-medium text-[#111]">All Segments</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] text-[#6b6b6b]">{total} Twins</span>
                        <span className="text-[12px] font-medium text-[#6b6b6b]">{dollars(total * unitPrice)}</span>
                      </div>
                    </div>
                    {/* Archetype rows */}
                    {archs.map((a) => {
                      const checked = set.has(a.archetype_id);
                      return (
                        <div
                          key={a.archetype_id}
                          onClick={() => toggleArch(slug, a.archetype_id)}
                          className={`flex cursor-pointer items-center justify-between border-b border-black/10 px-4 py-2.5 last:border-b-0 ${checked ? 'bg-[#E8FCFA]' : 'hover:bg-[#f5f5f3]'}`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Checkbox checked={checked} />
                            <span className="text-[13px] text-[#111]">{a.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[12px] text-[#6b6b6b]">{a.twin_count} Twins</span>
                            <span className="text-[12px] font-medium text-[#6b6b6b]">{dollars(a.twin_count * unitPrice)}</span>
                          </div>
                        </div>
                      );
                    })}
                    {archs.length === 0 && (
                      <div className="px-4 py-3 text-[13px] text-[#999]">
                        {cache[slug] === undefined ? 'Loading Segments…' : 'No Segments available for this Category.'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Checkout bar */}
            <div className="flex flex-col gap-4 rounded-xl border border-black/10 p-4 md:flex-row md:flex-wrap md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-4 md:gap-6">
                <div>
                  <div className="text-[11px] text-[#6b6b6b]">AI Consumer Twins selected</div>
                  <div className={`text-[22px] font-medium text-[#111] transition-opacity ${countLoading ? 'opacity-50' : ''}`}>{twins}</div>
                  {new Set(totals.selections.map((s) => s.sub_category)).size > 1 && (
                    <div className="mt-0.5 max-w-[220px] text-[10px] leading-snug text-[#9CA3AF]">
                      Some people belong to more than one category — they’re counted (and billed) once.
                    </div>
                  )}
                </div>
                <div className="h-8 w-px bg-black/10" />
                <div>
                  <div className="text-[11px] text-[#6b6b6b]">Total cost</div>
                  <div className={`text-[22px] font-medium text-[#111] transition-opacity ${countLoading ? 'opacity-50' : ''}`}>{dollars(cost)}</div>
                </div>
                <div className="flex flex-wrap gap-1 md:max-w-[220px]">
                  {totals.tags.slice(0, 3).map((t, i) => (
                    <Tag key={i}>{t.label} · {t.n}</Tag>
                  ))}
                  {totals.tags.length > 3 && <Tag>+{totals.tags.length - 3} more</Tag>}
                </div>
              </div>
            </div>
          </MktCard>

          <WizardNav
            onBack={() => setScreen(1)}
            onContinue={onStart}
            continueDisabled={!canStart}
            hint={
              twins === 0 ? (
                'Select at least one Segment to continue'
              ) : twins < MIN_RECOMMENDED_TWINS && !countLoading ? (
                <span className="text-amber-700">
                  Small samples may be less statistically reliable — we recommend at least {MIN_RECOMMENDED_TWINS} Twins.
                </span>
              ) : undefined
            }
          />
        </>
      )}
    </MarketplaceShell>
  );
};

export default BuildAudienceScreen;
